import isEqual from 'lodash.isequal';
import { makeObservable, toJS } from 'mobx';

import type { Attributes, TConstraints } from '@feathr/rachis';

import type { TEmailABTestVariation } from './mailrail_ab_test';
import { MailrailBaseEmailCampaign } from './mailrail_email_base';
import type { IMailrailSingleSendEmailCampaign, IMailrailSingleSendMailing } from './types';
import { EMailrailMailingKind } from './types';

export class MailrailSingleSendEmailCampaign<
  T extends IMailrailSingleSendEmailCampaign = IMailrailSingleSendEmailCampaign,
> extends MailrailBaseEmailCampaign<T> {
  public override get constraints(): TConstraints<IMailrailSingleSendEmailCampaign> {
    return {
      ...super.constraints,
      // Single Sends run all at once, so they do not have end dates
      date_end: undefined,
      segments_included: {
        length: {
          minimum: 1,
          tooShort: '^Add at least one included group',
        },
        list: {
          presence: {
            allowEmpty: false,
            message: '^Finish selecting a group or remove the placeholder group.',
          },
        },
        duplicates: {
          maximum: 0,
          message: '^Groups must be unique.',
        },
      },
    };
  }

  constructor(attributes: Partial<T>) {
    super(attributes);

    makeObservable(this);
  }

  // Versions B onward of the A/B test, in version order.
  public getVariantMailings(): IMailrailSingleSendMailing[] {
    return (this.get('mailings') ?? []).filter(({ kind }) => kind === EMailrailMailingKind.Variant);
  }

  // Merges an edit into one variant mailing; an edit typed back to the saved value reads clean again.
  public setVariantMailing(mailingId: string, patch: Partial<IMailrailSingleSendMailing>): void {
    const mailings: IMailrailSingleSendMailing[] = toJS(this.get('mailings')) ?? [];
    const nextMailings = mailings.map((mailing) =>
      mailing.mailing_id === mailingId ? { ...mailing, ...patch } : mailing,
    );
    this.set({ mailings: nextMailings } as Partial<Attributes<this>>);
    if (isEqual(nextMailings, toJS(this.shadowAttributes.mailings))) {
      this.setAttributeClean('mailings');
    }
  }

  public override toDirtyJS(): Partial<T> {
    const { mailings, ...dirty } = super.toDirtyJS();
    if (!mailings) {
      return dirty as Partial<T>;
    }
    const savedMailings: IMailrailSingleSendMailing[] = toJS(this.shadowAttributes.mailings) ?? [];
    const mailingEdits = mailings
      .filter(({ kind }) => kind === EMailrailMailingKind.Variant)
      .map((mailing) => {
        const saved = savedMailings.find(
          (savedMailing) => savedMailing.mailing_id === mailing.mailing_id,
        );
        const changes = Object.fromEntries(
          Object.entries(mailing).filter(
            ([key, value]) => !isEqual(value, saved?.[key as keyof IMailrailSingleSendMailing]),
          ),
        );
        return { ...changes, mailing_id: mailing.mailing_id };
      })
      .filter((edit) => Object.keys(edit).length > 1);
    return (mailingEdits.length ? { ...dirty, mailings: mailingEdits } : dirty) as Partial<T>;
  }

  public async copyVariation(
    from: TEmailABTestVariation,
    to: TEmailABTestVariation,
  ): Promise<this> {
    this.checkReplaced();
    this.assertCollection(this.collection);

    if (this.id) {
      return this.collection.copyVariation(this.id, from, to) as Promise<this>;
    }

    throw new Error('Models need to have ids to copy a variation');
  }

  protected override getEmbeddedTemplateIds(): Array<string | undefined> {
    return this.getVariantMailings().map((mailing) => mailing.template_id);
  }
}
